import { DEFAULT_HEADER_NAV_LINKS } from '~/landing_page/components/HeaderNav/HeaderNav';
import { handleInPageHashLinkClick, handleLandingLogoHomeClick } from '~/landing_page/lib/inPageNav';
import { cn } from '~/lib/utils';
import styles from './FooterSection.module.css';

export interface FooterLink {
  label: string;
  href: string;
  external?: boolean;
}

export interface FooterColumn {
  heading: string;
  links: FooterLink[];
}

// The nav column is the header's own list, so the two stay identical by construction. The Social
// column is gone: the same three links already sit as icons next to the copyright below.
const DEFAULT_FOOTER_COLUMNS: FooterColumn[] = [
  {
    heading: 'Product',
    links: DEFAULT_HEADER_NAV_LINKS.map((link) => ({ label: link.label, href: link.href })),
  },
  {
    heading: 'Legal',
    links: [
      { label: 'Terms of Service', href: '/terms' },
      { label: 'Privacy Policy', href: '/privacy' },
    ],
  },
];

export interface FooterSectionProps {
  className?: string;
  tagline?: string;
  copyright?: string;
  columns?: FooterColumn[];
  logoSrc?: string;
  /** `dark` is the landing v2 ground: the mark and the social glyphs invert. */
  tone?: 'light' | 'dark';
  logoAlt?: string;
  linkedInHref?: string;
  twitterHref?: string;
  youTubeHref?: string;
}

export function FooterSection({
  className,
  tagline = 'Built for people who love storytelling',
  copyright = 'Deck AI © 2026. All rights reserved.',
  columns = DEFAULT_FOOTER_COLUMNS,
  logoSrc = '/landing/logo/deck-symbol.svg',
  logoAlt = 'Deck AI',
  linkedInHref = '#',
  twitterHref = '#',
  youTubeHref = '#',
  tone = 'light',
}: FooterSectionProps) {
  return (
    <footer className={cn(styles.footer, tone === 'dark' && styles.footerDark, className)} data-name="FooterSection">
      <div className={styles.container}>
        <div className={styles.top}>
          <div className={styles.brand}>
            <a
              className={styles.logoLink}
              href="/"
              aria-label="Deck home"
              onClick={handleLandingLogoHomeClick}
            >
              <img className={styles.logo} src={logoSrc} alt={logoAlt} />
            </a>
            <p className={styles.tagline}>{tagline}</p>
          </div>

          <nav className={styles.columns} aria-label="Footer navigation">
            {columns.map((col, index) => (
              <div key={`${col.heading}-${index}`} className={styles.column}>
                <p className={styles.columnHeading}>{col.heading}</p>
                <ul className={styles.columnLinks} role="list">
                  {col.links.map((link, lIndex) => (
                    <li key={`${link.label}-${lIndex}`}>
                      {link.external ? (
                        <a
                          href={link.href}
                          className={styles.columnLink}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {link.label}
                        </a>
                      ) : (
                        <a
                          href={link.href}
                          className={styles.columnLink}
                          onClick={(e) => handleInPageHashLinkClick(e, link.href)}
                        >
                          {link.label}
                        </a>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </nav>
        </div>

        <div className={styles.divider} role="separator" />

        <div className={styles.bottom}>
          <p className={styles.copyright}>{copyright}</p>
          <div className={styles.social}>
            <a
              href={linkedInHref}
              className={styles.socialLink}
              aria-label="LinkedIn"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img
                className={styles.socialIcon}
                src="/landing/footer/LinkedIn.svg"
                alt=""
              />
            </a>
            <a
              href={twitterHref}
              className={styles.socialLink}
              aria-label="Twitter / X"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img
                className={styles.socialIcon}
                src="/landing/footer/Twitter.svg"
                alt=""
              />
            </a>
            <a
              href={youTubeHref}
              className={styles.socialLink}
              aria-label="YouTube"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img
                className={styles.socialIcon}
                src="/landing/footer/YouTube.svg"
                alt=""
              />
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}

FooterSection.displayName = 'FooterSection';
